// "Before you dial" (owner, 4 Nov 2026): who called this customer last, and is
// anyone on a call with them right now? One proxy read (GET /api/tc/call-status,
// server/telecaller/callStatus.js) used by the pre-dial question in dialViaPhone,
// the Last-call banner and the phone screens.
//
// The check is advice, never a gate: if it cannot be read the call still goes
// ahead — the server's reservation remains the one hard lock against two agents
// dialling the same customer at once.

const PROXY = (import.meta.env?.VITE_PROXY_URL || (import.meta.env?.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

/** Answer from the DialTracker card to a pre-dial question: { id, go }. */
export const DIAL_CONFIRM_EVENT = 'daksha:dial-confirm';

const m10 = (m) => String(m || '').replace(/\D/g, '').slice(-10);

/** The status for one number, or null when it cannot be read (no access, network, slow). */
export async function fetchCallStatus(mobile, { timeoutMs = 3000 } = {}) {
    const m = m10(mobile);
    if (m.length !== 10) return null;
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), timeoutMs) : null;
    try {
        const { authHeaders } = await import('../api/authHeaders');
        const r = await fetch(`${PROXY}/api/tc/call-status?mobile=${m}`, { headers: await authHeaders(), signal: ctl?.signal });
        const j = await r.json().catch(() => null);
        return r.ok && j?.ok ? j : null;
    } catch { return null; }
    finally { if (timer) clearTimeout(timer); }
}

const MIN = 60_000;

/** "18 min ago", "3 h ago", "yesterday", "29 Oct" — short, for a busy desk. */
export function ago(at, now = Date.now()) {
    const d = now - Number(at || 0);
    if (!at || d < 0) return '';
    if (d < MIN) return 'just now';
    if (d < 60 * MIN) return `${Math.floor(d / MIN)} min ago`;
    if (d < 24 * 60 * MIN) return `${Math.floor(d / (60 * MIN))} h ago`;
    if (d < 48 * 60 * MIN) return 'yesterday';
    return `${Math.floor(d / (24 * 60 * MIN))} days ago`;
}

/** "29 Oct, 05:45 pm" in India time. */
export const when = (at) => (at ? new Date(Number(at)).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '');

const talk = (sec) => (sec > 0 ? ` · talked ${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}` : '');
const who = (c) => (c?.self ? 'you' : (c?.by || 'the team'));

/** What happened on a call, in a few words: "Not Answered", "📥 Missed (customer rang)". */
export function resultText(c) {
    if (!c) return '';
    if (c.direction === 'missed') return 'Missed — customer rang us';
    const r = c.result || (c.durationSec > 0 ? 'Connected' : 'Called');
    return `${c.direction === 'incoming' ? '📥 ' : ''}${r}${talk(c.durationSec)}`;
}

/** "29 Oct, 05:45 pm (18 min ago) by SREE KALA — Not Answered" */
export function callLine(c, now = Date.now()) {
    if (!c) return '';
    const a = ago(c.at, now);
    return `${when(c.at)}${a ? ` (${a})` : ''} by ${who(c)} — ${resultText(c)}`;
}

const STATE_WORDS = { on_call: 'is on a call with', ringing: 'is ringing', dialing: 'is dialling', claimed: 'has picked this customer to call' };

/** "Sree Kala is on a call with this customer now (2 min)" */
export function busyLine(b, now = Date.now()) {
    if (!b) return '';
    const mins = Math.max(0, Math.round((Number(b.sinceMs) || 0) / MIN));
    const verb = STATE_WORDS[b.state] || 'is on';
    const tail = b.state === 'claimed' ? '' : ' this customer';
    return `${b.by || 'Another agent'} ${verb}${tail} now${mins ? ` (${mins} min)` : ''}`;
}

/** "3 calls in the last 24 h — by Sree Kala, Anu" (only other agents' calls). */
export function windowLine(s) {
    const w = s?.window;
    if (!w || !w.byOthers) return '';
    return `${w.byOthers} call${w.byOthers === 1 ? '' : 's'} by other agents in the last ${w.hours} h — ${w.otherAgents.join(', ')}`;
}

/** Plain text for window.confirm (phone screens, or a page without the DialTracker card). */
export function confirmText(s, now = Date.now()) {
    if (!s?.warn) return '';
    const lines = [];
    if (s.busy) lines.push(`⚠ ${busyLine(s.busy, now)}.`);
    if (s.lastCall) lines.push(`Last call: ${callLine(s.lastCall, now)}${s.lastCall.remark ? `\n“${s.lastCall.remark}”` : ''}`);
    const w = windowLine(s);
    if (w) lines.push(w);
    lines.push('', s.busy ? 'Call anyway? They may be talking right now.' : 'Call anyway?');
    return lines.join('\n');
}
